import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {
  GPLU_WHOLE_LIFE_FIELD_ID,
  GPLUCoverageCode,
  GPLUCoverageDefinitionType,
} from '../../../PlanTemplates/GWL/gpluConstants';
import {getAmountValuePath, getGPLUOptionsBlockPropertyPaths} from '../PropertyPaths';
import {Map as IMap, List} from 'immutable';

const AccelDeathBenefit = () => {
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, selectedCoverageIndex} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getGPLUOptionsBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);
  const spouseCoverageIndex = getQuoteFieldValue<List<IMap<string, any>>>(
    `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}].coverageDefs`
  )?.findIndex((cov: any) => {
    return cov.get('_type') === GPLUCoverageDefinitionType.SPOUSE_WL;
  });

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-9df56b5a-3bcd-43a2-a579-05811b4cbfa9'},
      items: [
        {
          key: '45b00e31-7da3-4d5b-9408-31dbbe80a302',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Accelerated Benefit Maximum Percentage"
              id={propertyPaths.maxAcceleratedBenefitPct}
              value={getQuoteFieldValue(propertyPaths.maxAcceleratedBenefitPct)}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: propertyPaths.maxAcceleratedBenefitPct, newValue: Number(e.target.value) / 100},
                  spouseCoverageIndex > -1 && {
                    path: getGPLUOptionsBlockPropertyPaths(selectedPlan?.planIndex, spouseCoverageIndex)
                      .maxAcceleratedBenefitPct,
                    newValue: Number(e.target.value) / 100,
                  },
                ])
              }
              type="number"
              adornment="percentage"
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                } else {
                  const currentValue = (e.target as HTMLInputElement).value;
                  const updatedValue = Number(currentValue + e.key);
                  if (updatedValue > 100) {
                    e.preventDefault();
                  }
                }
              }}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.maxAcceleratedBenefitPct}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: '0046ecac-9e05-421d-a1f4-ca406934055b',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Accelerated Benefit Maximum Amount"
              id={propertyPaths.maxAcceleratedBenefitAmount}
              value={getQuoteFieldValue(getAmountValuePath(propertyPaths.maxAcceleratedBenefitAmount))}
              onChange={e =>
                updateQuoteFieldValues([
                  {
                    path: getAmountValuePath(propertyPaths.maxAcceleratedBenefitAmount),
                    newValue: Number(e.target.value),
                  },
                  spouseCoverageIndex > -1 && {
                    path: getAmountValuePath(
                      getGPLUOptionsBlockPropertyPaths(selectedPlan?.planIndex, spouseCoverageIndex)
                        .maxAcceleratedBenefitAmount
                    ),
                    newValue: Number(e.target.value),
                  },
                ])
              }
              onKeyDown={e => {
                if (['.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              formFieldId={GPLU_WHOLE_LIFE_FIELD_ID.maxAcceleratedBenefitAmount}
              type="number"
              adornment="money"
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];
  return <FormRowGrid rows={rows} />;
};

export const GPLUOptionsBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan, selectedCoverageIndex} = useSelector((state: ReducerState) => state.quoteForm);
  const propertyPaths = getGPLUOptionsBlockPropertyPaths(selectedPlan?.planIndex, selectedCoverageIndex);

  const rows1: GridRowProps[] = [
    {
      rowProps: {key: 'row-60ea6800-10df-4b66-b955-670f1b92d13d'},
      items: [
        {
          key: 'af6cef09-9c2a-4d36-a02d-2daaf355619e',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Non-Forfeiture Default Option"
              id={propertyPaths.nonForfeitureDefaultOptionCd}
              value={getQuoteFieldValue(propertyPaths.nonForfeitureDefaultOptionCd)}
              onChange={e => updateQuoteFieldValue(propertyPaths.nonForfeitureDefaultOptionCd, e.target.value)}
              dropdownVariant="AttributeValue"
              searchKey="WLCoverage.nonForfeitureDefaultOptionCd"
              coverageCode={GPLUCoverageCode.WHOLE_LIFE}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  const rows2: GridRowProps[] = [
    {
      rowProps: {key: 'row-6fff4203-5dff-4372-a616-848ce2071423'},
      items: [
        {
          key: '1dd4ddb5-b24b-4cce-9b6b-881abaf8267c',
          element: (
            <QuoteFormElement
              elementType="FormControl"
              label="PORTABILITY"
              id={propertyPaths.isPortabilityApplied}
              checked={getQuoteFieldValue<boolean>(propertyPaths.isPortabilityApplied)}
              onChange={e => updateQuoteFieldValue(propertyPaths.isPortabilityApplied, e.target.checked)}
              controlType="checkbox"
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  const additionalOptNBenefits: ConditionalSection[] = [
    {
      Component: AccelDeathBenefit,
      key: 'accelDeathBenefit',
      label: 'ACCEL DEATH BENEFIT FOR TERMINAL ILLNESS OR CONDITION RIDER',
      disabled: true,
      isApplied: getQuoteFieldValue(propertyPaths.isAccelerateBenefitRequired) || true,
    },
  ];

  return (
    <FormWrapper title="Options" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows1} />
        <QuoteFormConditionalSection sections={additionalOptNBenefits} />
        <FormRowGrid rows={rows2} />
      </Grid>
    </FormWrapper>
  );
};
